import React, { useEffect, useState } from 'react';
import { Badge, Flex, HStack, Text } from '@chakra-ui/react';
import { useTimezone } from '../../contexts/TimezoneContext';
import { formatTimeWithSecondsInTz } from '../../utils/timezone';
import { PointSituation } from '../../utils/tennisScore';
import { BoardPlayer } from './tennisBoard';

/** A gap since the last point longer than this is unusual (99 % of gaps are under 41 s). */
const FEED_AGE_WARN_SECONDS = 45;
/** A gap longer than this means the feed or the page has most likely stopped. */
const FEED_AGE_ALERT_SECONDS = 90;
const SECONDS_PER_MINUTE = 60;
const TICK_MS = 1000;

export type MatchState = 'PREMATCH' | 'LIVE' | 'FINISHED' | 'CANCELLED';

interface TennisMatchStatusProps {
  state: MatchState;
  /** Where the match stands, in words. */
  where: string;
  tiebreak: boolean;
  situation?: PointSituation;
  players: [BoardPlayer, BoardPlayer];
  /** Who serves the next point; the other player holds any break point. */
  serverId?: number;
  lastPointAt?: number;
}

const nameOf = (players: [BoardPlayer, BoardPlayer], id?: number) =>
  players.find(p => p.competitor.id === id)?.competitor.name ?? '';

const ageText = (seconds: number) => seconds >= SECONDS_PER_MINUTE
  ? `${Math.floor(seconds / SECONDS_PER_MINUTE)} min ago`
  : `${seconds} s ago`;

const ageScheme = (seconds: number) =>
  seconds > FEED_AGE_ALERT_SECONDS ? 'red' : seconds > FEED_AGE_WARN_SECONDS ? 'orange' : 'gray';

/** How long ago the last point was, ticking every second. */
const FeedAge: React.FC<{ since: number }> = ({ since }) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), TICK_MS);
    return () => clearInterval(timer);
  }, []);
  const seconds = Math.max(0, Math.floor((now - since) / TICK_MS));
  return (
    <Badge colorScheme={ageScheme(seconds)} variant="subtle" fontSize="xs" textTransform="none">
      {ageText(seconds)}
    </Badge>
  );
};

/**
 * The match state in one line: live or not, where the match stands, what the next point can decide —
 * break, set or match point, deuce, tiebreak — and when the last point was played.
 */
export const TennisMatchStatus: React.FC<TennisMatchStatusProps> = ({
  state,
  where,
  tiebreak,
  situation,
  players,
  serverId,
  lastPointAt
}) => {
  const [timeZone] = useTimezone();
  const live = state === 'LIVE';
  const receiver = serverId !== undefined ? players.find(p => p.competitor.id !== serverId) : undefined;

  return (
    <Flex align="center" gap={2} flexWrap="wrap" mb={3}>
      {state === 'PREMATCH' && <Badge fontSize="xs">Prematch</Badge>}
      {live && <Badge colorScheme="green" fontSize="xs">● Live</Badge>}
      {state === 'FINISHED' && <Badge colorScheme="purple" fontSize="xs">Finished</Badge>}
      {state === 'CANCELLED' && <Badge colorScheme="red" fontSize="xs">Cancelled</Badge>}
      {live && tiebreak && <Badge colorScheme="orange" fontSize="xs">Tiebreak</Badge>}
      {live && situation?.matchPointFor !== undefined && (
        <Badge colorScheme="red" variant="solid" fontSize="xs">Match point · {nameOf(players, situation.matchPointFor)}</Badge>
      )}
      {live && situation?.setPointFor !== undefined && (
        <Badge colorScheme="red" variant="solid" fontSize="xs">Set point · {nameOf(players, situation.setPointFor)}</Badge>
      )}
      {live && receiver && situation && situation.breakPoints > 0 && (
        <Badge colorScheme="red" fontSize="xs">
          Break point{situation.breakPoints > 1 ? ` ×${situation.breakPoints}` : ''} · {receiver.competitor.name}
        </Badge>
      )}
      {live && situation?.deuce && situation.breakPoints === 0 && <Badge colorScheme="yellow" fontSize="xs">Deuce</Badge>}
      <Text fontSize="sm" color="gray.600">{where}</Text>
      {lastPointAt !== undefined && (
        <HStack spacing={2} ml="auto" fontSize="xs" color="gray.600">
          <Text>Last point <Text as="span" fontWeight="semibold" color="gray.800">{formatTimeWithSecondsInTz(lastPointAt, timeZone)}</Text></Text>
          {live && <FeedAge since={lastPointAt} />}
        </HStack>
      )}
    </Flex>
  );
};

export default TennisMatchStatus;
